import { createElement } from 'react';
import activeHtml from 'react-active-html';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';

export const universalActiveHtml = (html, componentMap) => {
  if (process.env.BUILD_TARGET === 'server') {
    const xmldom = require('xmldom');
    activeHtml.DOMParser = new xmldom.DOMParser();
  }

  return activeHtml(html, componentMap);
};

if (!process.env.RAZZLE_CMS_SITE) {
  throw new Error(
    'The RAZZLE_CMS_SITE environment variable is required but was not specified.'
  );
}

export function getSiteRoutes() {
  // Load routes.js from the matching sites directory if available.
  const routes = require(`../../sites/${
    process.env.RAZZLE_CMS_SITE
  }/routes.js`);
  return routes;
}

export function formatPrice(price, countryCode, currencyCode) {
  if (!countryCode || !currencyCode) {
    return price;
  }
  return new Intl.NumberFormat(countryCode, {
    style: 'currency',
    currency: currencyCode
  }).format(price);
}

export function wrapMultiline(string, splitBy = '\n', wrapWith = 'p') {
  const parts = string.split(splitBy);

  return parts.map(part => createElement(wrapWith, [], part));
}

export const analytics =
  typeof window !== 'undefined' && window.analytics
    ? window.analytics
    : {
        track: () => null,
        identify: () => null,
        page: () => null
      };

export const getCookieDomain = () =>
  process.env.RAZZLE_COOKIE_DOMAIN
    ? process.env.RAZZLE_COOKIE_DOMAIN
    : '.formlabs.com';

export const handleRedirect = (res, status) => {
  // Server-side, update status codes and redirect if necessary
  if (res) {
    if (status.location) {
      res.redirect(status.code, status.location);
    } else {
      res.status(status.code);
    }
  } else {
    // client-side redirect
    if (status.location) {
      const redirectLocation = status.location;
      return { redirectLocation };
    }
  }
};

export const handleCountrySiteRedirect = (
  req,
  res,
  detectedLocale,
  country,
  countries
) => {
  // only on server
  if (!req || !res || !country) {
    return null;
  }
  const hasLocaleCookie = has(req, 'cookies.locale');

  const detectedRegionCode = get(detectedLocale, 'region.code', null);
  const detectedLanguages = filter(
    get(detectedLocale, 'languages', []),
    language => language.length === 2
  );
  const matchedCountrySite = find(
    countries,
    country =>
      country.code === detectedRegionCode &&
      detectedLanguages.includes(country.language)
  );

  if (
    !hasLocaleCookie && // only for first request in this session
    matchedCountrySite &&
    country.path === req.path &&
    matchedCountrySite.path !== req.path
  ) {
    res.redirect(302, matchedCountrySite.path);
    return true;
  }
  return null;
};

// Sort array by values respecting the natural locale specific order
export const sortWithLocale = (items, locale) =>
  items.sort((a, b) =>
    a.text.localeCompare(b.text, locale, { ignorePunctuation: true })
  );

// Compare values respecting the natural locale specific order
// For use in sorting applications where the above function is too specific
export const compareWithLocale = (a, b, locale) =>
  a.localeCompare(b, locale, { ignorePunctuation: true });

// Some nice math functions
export const mod = (n, m) => {
  return ((n % m) + m) % m;
};
export const slope = (x1, y1, x2, y2) => {
  return (y2 - y1) / (x2 - x1);
};
export const distance = (x1, y1, x2, y2) => {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

// turn object into query string
export const querystringify = params =>
  Object.entries(params)
    .map(([key, value]) =>
      Array.isArray(value)
        ? value.reduce(
            (acc, v) =>
              `${acc ? `${acc}&` : ''}${key}=${encodeURIComponent(v)}`,
            ''
          )
        : `${key}=${encodeURIComponent(value)}`
    )
    .join('&');

// Universal GraphQL API Url
const internalGraphqlApiUrl = process.env.RAZZLE_internal_content_api_uri_base;
const publicGraphqlApiUrl =
  process.env.RAZZLE_public_graphql_uri ||
  process.env.RAZZLE_content_api_uri_base;
export const graphqlApiUrl = `${
  process.env.BUILD_TARGET === 'server' && internalGraphqlApiUrl
    ? internalGraphqlApiUrl
    : publicGraphqlApiUrl
}/graphql`;
